import { useEffect, useState } from 'react';
import { useFormat } from '@/lib/format';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Plus,
  MagnifyingGlass as Search,
  DownloadSimple as Download,
  Wallet,
  Receipt,
  TrendUp as TrendingUp,
  WarningCircle,
  PlayCircle,
  Timer,
  CircleNotch,
} from '@phosphor-icons/react';
import { PageHeader } from '@/components/shared/PageHeader';
import { QueryErrorState } from '@/components/shared/QueryErrorState';
import { RowActions } from '@/components/shared/RowActions';
import { TablePagination, pageSlice } from '@/components/shared/TablePagination';
import { NewFeeNoteModal } from '@/components/shared/NewFeeNoteModal';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { StatCard } from '@/components/shared/StatCard';
import { useTimeEntriesStore } from '@/lib/timeEntriesStore';
import { fetchFeeNotes, fetchAgedDebt, fetchFeeNoteStats, STATUS_LABELS as FEE_NOTE_STATUS_LABEL, type FeeNoteStatus, type FeeNoteListRow, type AgedDebtRow } from '@/lib/feeNotesApi';
import { api } from '@/lib/api';
import { fetchActivityTotals } from '@/lib/activitiesApi';
import { useAuthStore } from '@/features/auth/store';
import { canMutateFinancials } from '@/lib/pupilUiBoundary';
import { useChambersMode } from '@/lib/useChambersMode';
import { toast } from 'sonner';
import { exportToCsv } from '@/lib/exportUtils';
import { useTimerStore } from '@/lib/timerStore';
import { FeeNoteActionsButton } from '@/components/fee-notes/FeeNoteActionsButton';
import { fetchFeesSummaryByMatter, type FeeMatterSummaryRow } from '@/lib/feesApi';

type Tab = 'records' | 'notes' | 'outstanding' | 'matter';

function FeeNoteDownloadButton({ feeNote }: { feeNote: FeeNoteListRow }) {
  const [busy, setBusy] = useState(false);
  const handleDownload = async () => {
    setBusy(true);
    try {
      const res = await api.get(`/fee-notes/${feeNote.id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data as Blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${feeNote.feeNoteNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch {
      toast.error('Could not open fee note');
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      disabled={busy}
      onClick={handleDownload}
      title="Download PDF"
      className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40"
    >
      {busy ? <CircleNotch size={14} className="animate-spin" /> : <Download size={14} />}
    </button>
  );
}


function feeNoteStatusVariant(s: FeeNoteStatus): 'default' | 'success' | 'warning' | 'danger' | 'info' | 'gold' | 'outline' {
  switch (s) {
    case 'draft': return 'default';
    case 'pending_approval': return 'warning';
    case 'approved': return 'info';
    case 'sending': return 'info';        // in-flight dispatch
    case 'sent': return 'info';
    case 'partially_paid': return 'gold';
    case 'paid': return 'success';
    case 'overdue': return 'danger';
    case 'cancelled': return 'outline';
    case 'written_off': return 'outline';
    case 'send_failed': return 'danger';  // alert: email dispatch failed
    case 'partially_credited': return 'gold';
    case 'credited': return 'success';
  }
}

export default function MyFeesPage() {
  // Dates and money are written the way this person asked for; the currency stays the
  // practice's — a locale decides separators, never what someone is owed.
  const { formatDate, formatCurrency } = useFormat();
  const startTimer = useTimerStore((s) => s.start);
  const timerRunning = useTimerStore((s) => s.running);
  const [tab, setTab] = useState<Tab>('records');
  const [recordSearch, setRecordSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  useEffect(() => { setPage(1); }, [tab, recordSearch]);
  const [feeNoteOpen, setFeeNoteOpen] = useState(false);
  const navigate = useNavigate();

  // Email → billing bridge: /barrister/fees?matterId=…&source=email opens the
  // fee-note modal pre-filled with that matter so a fee can be raised straight
  // from a communication.
  const [searchParams, setSearchParams] = useSearchParams();
  const emailMatterId = searchParams.get('source') === 'email' ? searchParams.get('matterId') : null;
  useEffect(() => {
    if (emailMatterId) setFeeNoteOpen(true);
  }, [emailMatterId]);
  const user = useAuthStore((s) => s.user);
  const { isSolo } = useChambersMode();
  const timeEntries = useTimeEntriesStore((s) => s.entries);
  const deleteTimeEntry = useTimeEntriesStore((s) => s.deleteEntry);
  const fetchEntries = useTimeEntriesStore((s) => s.fetchEntries);

  // Fee notes scoped to this barrister via the barristerId filter
  const myFeeNotesQ = useQuery({
    queryKey: ['my-fee-notes', user?.barristerId],
    queryFn: () => fetchFeeNotes({ barristerId: user?.barristerId ?? '', limit: 100 }),
    enabled: !!user?.barristerId,
  });

  // MTD activity totals for this user
  const now = new Date();
  const mtdStart = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
  const mtdTotalsQ = useQuery({
    queryKey: ['my-activity-totals', user?.id, mtdStart],
    queryFn: () => fetchActivityTotals({ firmUserId: user?.id, dateFrom: mtdStart }),
    enabled: !!user?.id,
  });

  // Aged debt for outstanding balances tab
  const agedDebtQ = useQuery({
    queryKey: ['my-aged-debt'],
    queryFn: fetchAgedDebt,
    enabled: tab === 'outstanding',
  });

  // Outstanding / overdue / collection — SERVER-calculated over ALL fee notes (caller-scoped),
  // never client-side over the loaded page. Reads the same canonical money endpoint the admin
  // Fees page uses, so the headline numbers can't disagree with the aged-debt table (Rule #14).
  const feeStatsQ = useQuery({
    queryKey: ['my-fee-note-stats'],
    queryFn: fetchFeeNoteStats,
    staleTime: 30_000,
  });
  const feeStats = feeStatsQ.data ?? { outstanding: 0, outstandingCount: 0, overdue: 0, collectionRate: 0 };

  // ── A REFUSED READ IS NOT A FAILED ONE ────────────────────────────────────────────────────
  // `/fee-notes/stats` and `/fee-notes/aged-debt` are both gated by FINANCIAL_VIEW_ROLES
  // (fee-notes.routes.ts), which deliberately excludes pupil and door_tenant: "Pupils observe work
  // but have no billing authority". The rest of this page — their own fee notes, their own time
  // entries, MTD totals — is theirs to read and still loads.
  //
  // So for those roles the two tiles showed "—" with "Couldn't load", and the Outstanding tab
  // offered a Retry on a decision that can never change. Same defect, same fix as MyReportsPage:
  // separate the 403 from a real fault, name it, and offer no retry. This reads the server's
  // answer; it does not decide access, and it invents no figure in place of one.
  const statusOf = (q: { error: unknown }) =>
    (q.error as { response?: { status?: number } } | null)?.response?.status;
  const feeStatsRefused = feeStatsQ.isError && statusOf(feeStatsQ) === 403;
  const agedDebtRefused = agedDebtQ.isError && statusOf(agedDebtQ) === 403;
  const ROLE_REFUSED_HINT = 'Not available to your role';

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  const handleExport = () => {
    if (tab === 'records') {
      if (timeEntries.length === 0) {
        toast.info('No time entries to export');
        return;
      }
      exportToCsv(
        timeEntries.map((e) => ({
          date: e.date,
          description: e.description,
          hours: e.hours,
          matter: e.matterLabel ?? '',
          status: e.status,
        })),
        'time-entries-export',
        [
          { key: 'date', label: 'Date' },
          { key: 'description', label: 'Description' },
          { key: 'hours', label: 'Hours' },
          { key: 'matter', label: 'Matter' },
          { key: 'status', label: 'Status' },
        ],
      );
      toast.success(`Exported ${timeEntries.length} time entries`);
    } else if (tab === 'notes') {
      const notes = myFeeNotesQ.data?.feeNotes ?? [];
      if (notes.length === 0) {
        toast.info('No fee notes to export');
        return;
      }
      // Accountant-format export: includes matter ref, VAT, net, gross, balance per note
      exportToCsv(
        notes.map((n) => ({
          number:       n.feeNoteNumber,
          matter_ref:   n.case?.caseNumber ?? '',
          matter_name:  n.case?.caseName ?? '',
          issue_date:   formatDate(n.issueDate),
          due_date:     formatDate(n.dueDate),
          net_gbp:      Number(n.subtotal).toFixed(2),
          vat_gbp:      Number(n.vatTotal).toFixed(2),
          gross_gbp:    Number(n.total).toFixed(2),
          paid_gbp:     Number(n.amountPaid).toFixed(2),
          balance_gbp:  n.outstanding.toFixed(2), // server-authoritative, refund-aware
          status:       FEE_NOTE_STATUS_LABEL[n.status] ?? n.status,
        })),
        `fee-notes-accountant-${new Date().toISOString().slice(0, 10)}`,
        [
          { key: 'number',      label: 'Fee Note #'    },
          { key: 'matter_ref',  label: 'Matter Ref'    },
          { key: 'matter_name', label: 'Matter'        },
          { key: 'issue_date',  label: 'Issue Date'    },
          { key: 'due_date',    label: 'Due Date'      },
          { key: 'net_gbp',     label: 'Net (£)'       },
          { key: 'vat_gbp',     label: 'VAT (£)'       },
          { key: 'gross_gbp',   label: 'Gross (£)'     },
          { key: 'paid_gbp',    label: 'Paid (£)'      },
          { key: 'balance_gbp', label: 'Balance (£)'   },
          { key: 'status',      label: 'Status'        },
        ],
      );
      toast.success(`Exported ${notes.length} fee notes (accountant format)`);
    } else {
      const debt = agedDebtQ.data ?? [];
      if (debt.length === 0) {
        toast.info('No outstanding debt to export');
        return;
      }
      exportToCsv(
        debt.map((d) => ({
          client: d.clientName,
          current: d.current,
          days_30: d.d30,
          days_60: d.d60,
          days_90_plus: d.d90,
          total: d.total,
        })),
        'aged-debt-export',
        [
          { key: 'client', label: 'Client' },
          { key: 'current', label: 'Current' },
          { key: 'days_30', label: '30 Days' },
          { key: 'days_60', label: '60 Days' },
          { key: 'days_90_plus', label: '90+ Days' },
          { key: 'total', label: 'Total' },
        ],
      );
      toast.success(`Exported ${debt.length} clients`);
    }
  };

  // View = open the fee-note PDF in a new tab (generates server-side on demand).
  // PDF download / send / credit flows remain in FeeNoteActionsButton / FeeNoteDownloadButton.
  const viewFeeNotePdf = async (id: string, feeNoteNumber: string) => {
    try {
      const res = await api.get(`/fee-notes/${id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data as Blob);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } catch {
      toast.error(`Could not open ${feeNoteNumber}`);
    }
  };

  return (
    <div>
      <PageHeader
        onBack={() => navigate(-1)}
        title="My Fees"
        subtitle="Your fee records, fee notes and outstanding balances — collected in one place"
        actions={
          <>
            <Button
              variant="outline"
              size="md"
              leftIcon={<PlayCircle className="h-4 w-4" />}
              onClick={() => { startTimer(); toast.success('Timer started — visible in the top bar'); }}
              disabled={timerRunning}
            >
              {timerRunning ? 'Timer running' : 'Start timer'}
            </Button>
            <Button variant="outline" size="md" leftIcon={<Download className="h-4 w-4" />} onClick={handleExport}>Export</Button>
            {/* Fees are READ-ONLY for a pupil: fee-notes and activities WRITE_ROLES both exclude
                them, so these controls only ever 403'd. */}
            {canMutateFinancials(user?.memberType) && (
              <Button variant="primary" size="md" leftIcon={<Plus className="h-4 w-4" />} onClick={() => setFeeNoteOpen(true)}>New fee note</Button>
            )}
          </>
        }
      />

      <div className="mb-6 grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        <StatCard
          icon={<Wallet className="h-4 w-4" />}
          label="Billed (MTD)"
          value={mtdTotalsQ.isLoading ? '...' : mtdTotalsQ.isError ? '—' : `£${((mtdTotalsQ.data?.billableAmount ?? 0) / 1000).toFixed(1)}k`}
        />
        {/* These three read feeStatsQ — a failed load shows "—", never a reassuring £0.0k, and a
            role REFUSAL says so instead of blaming the connection. */}
        <StatCard
          icon={<Receipt className="h-4 w-4" />}
          label="Outstanding"
          value={feeStatsQ.isLoading ? '...' : feeStatsQ.isError ? '—' : `£${(feeStats.outstanding / 1000).toFixed(1)}k`}
          hint={
            feeStatsRefused ? ROLE_REFUSED_HINT
              : feeStatsQ.isError ? "Couldn't load"
              : `${feeStats.outstandingCount} fee note${feeStats.outstandingCount !== 1 ? 's' : ''}`
          }
        />
        <StatCard
          icon={<WarningCircle className="h-4 w-4" />}
          label="Overdue"
          value={feeStatsQ.isLoading ? '...' : feeStatsQ.isError ? '—' : `£${(feeStats.overdue / 1000).toFixed(1)}k`}
          hint={feeStatsRefused ? ROLE_REFUSED_HINT : undefined}
        />
        <StatCard
          icon={<TrendingUp className="h-4 w-4" />}
          label="Collection rate"
          value={feeStatsQ.isLoading ? '...' : feeStatsQ.isError ? '—' : `${feeStats.collectionRate}%`}
          hint={feeStatsRefused ? ROLE_REFUSED_HINT : undefined}
        />
      </div>

      {/* Awaiting your approval — the clerk drafted these; the send is blocked until
          you approve. Surfaced so the approval gate never silently stalls. */}
      {(() => {
        const pending = (myFeeNotesQ.data?.feeNotes ?? []).filter((fn) => fn.status === 'pending_approval');
        if (pending.length === 0) return null;
        return (
          <div className="mb-5 rounded-lg border border-amber-200 dark:border-amber-500/30 bg-amber-50/60 dark:bg-amber-500/10 p-4">
            <div className="mb-2 flex items-center gap-2">
              <WarningCircle className="h-4 w-4 text-amber-600 dark:text-amber-400" weight="fill" />
              <span className="text-sm font-semibold text-amber-900 dark:text-amber-200">Fee notes awaiting your approval</span>
              <span className="rounded-full bg-amber-200 px-2 py-0.5 text-[11px] font-semibold text-amber-800 dark:text-amber-300">{pending.length}</span>
            </div>
            <div className="space-y-2">
              {pending.map((fn) => (
                <div key={fn.id} className="flex items-center justify-between gap-3 rounded-md border border-amber-200 dark:border-amber-500/30 bg-card px-3 py-2">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium text-foreground">{fn.feeNoteNumber} · {fn.case?.caseName ?? fn.case?.caseNumber ?? ''}</div>
                    <div className="text-xs text-muted-foreground">{formatCurrency(Number(fn.total))} · drafted by the clerk — review, then approve to send</div>
                  </div>
                  <FeeNoteActionsButton feeNote={fn} onChanged={() => myFeeNotesQ.refetch()} />
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Tabs */}
      {/* Scrollable at phone width — "By matter" used to sit past the right edge, unreachable. */}
      <div className="no-scrollbar -mb-px mb-4 flex items-center gap-1 overflow-x-auto overflow-y-hidden border-b border-border">
        {([
          { key: 'records',     label: 'Activities'           },
          { key: 'notes',       label: 'Fee notes'            },
          { key: 'outstanding', label: 'Outstanding balances' },
          { key: 'matter',      label: 'By matter'            },
        ] as const).map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`shrink-0 border-b-2 px-4 py-2.5 text-sm font-medium transition ${
              tab === t.key
                ? 'border-brand-600 text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'records' && (() => {
        const allRecords = [
          ...timeEntries.map((te) => ({
            id: te.id,
            date: te.date,
            case: te.matterLabel,
            matterId: te.matterId,
            kind: te.kind,
            hours: te.hours,
            amount: te.amount,
            status: te.status,
            vat: te.vat,
            description: te.description,
            fromTimer: true,
          })),
        ];

        const q = recordSearch.toLowerCase();
        const filtered = q
          ? allRecords.filter(
              (r) =>
                r.case.toLowerCase().includes(q) ||
                r.kind.toLowerCase().includes(q) ||
                r.description.toLowerCase().includes(q),
            )
          : allRecords;

        return (
          <Card>
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-4 py-3">
              <div className="flex min-w-0 flex-1 items-center gap-2">
                <Search className="h-4 w-4 text-muted-foreground" />
                <input
                  type="search"
                  placeholder="Filter by matter or kind…"
                  className="h-9 w-full min-w-0 bg-transparent text-sm placeholder:text-muted-foreground focus:outline-none sm:w-72"
                  value={recordSearch}
                  onChange={(e) => setRecordSearch(e.target.value)}
                />
              </div>
              <Button variant="outline" size="sm" className="shrink-0 whitespace-nowrap" onClick={() => navigate('/barrister/activities')}>Open Activities →</Button>
            </div>
            <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Matter</th>
                  <th>Kind</th>
                  <th>Description</th>
                  <th>Hours</th>
                  <th>Net</th>
                  <th>VAT</th>
                  <th>Status</th>
                  <th className="w-10 whitespace-nowrap">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-6 py-12 text-center">
                      <div className="text-sm text-muted-foreground">No fee activity recorded on your matters yet.</div>
                      <Button className="mt-4" variant="outline" size="sm" onClick={() => navigate('/barrister/activities')}>
                        Record activity
                      </Button>
                    </td>
                  </tr>
                ) : pageSlice(filtered, page, pageSize).map((r) => (
                  <tr key={r.id} className="cursor-pointer">
                    <td className="text-sm text-muted-foreground">{r.date}</td>
                    <td>
                      <button
                        className="text-sm font-medium text-brand-600 dark:text-brand-400 hover:underline"
                        onClick={(e) => { e.stopPropagation(); if (r.matterId) navigate(`/barrister/matters/${r.matterId}`); }}
                      >
                        {r.case}
                      </button>
                    </td>
                    <td className="flex items-center gap-1.5">
                      <Badge>{r.kind}</Badge>
                      {r.fromTimer && (
                        <Badge variant="outline" className="gap-0.5 text-[10px]">
                          <Timer className="h-3 w-3" />
                          Timer
                        </Badge>
                      )}
                    </td>
                    <td className="text-sm">{r.description}</td>
                    <td className="text-sm text-muted-foreground">{r.hours}</td>
                    <td className="tabular font-medium text-foreground">{formatCurrency(r.amount)}</td>
                    <td className="tabular text-xs text-muted-foreground">{formatCurrency(r.vat)}</td>
                    <td>
                      <Badge variant={r.status === 'Billable' ? 'info' : 'default'}>
                        {r.status}
                      </Badge>
                    </td>
                    <td>
                      {r.fromTimer && (
                        <RowActions
                          onDelete={!canMutateFinancials(user?.memberType) ? undefined : async () => {
                            // Only claim it is gone once the server says so — the store
                            // restores the row and explains itself if it refused.
                            if (await deleteTimeEntry(r.id)) toast.success('Time entry deleted');
                          }}
                          deleteConfirm={{
                            title: 'Delete time entry?',
                            message: 'Delete this time entry? This cannot be undone.',
                            confirmLabel: 'Delete',
                          }}
                        />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
            {filtered.length > 0 && (
              <TablePagination page={page} pageSize={pageSize} total={filtered.length}
                onPageChange={setPage} onPageSizeChange={setPageSize} />
            )}
          </Card>
        );
      })()}

      {tab === 'notes' && (
        <Card>
          <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th>Number</th>
                <th>Matter</th>
                <th>Net</th>
                <th>VAT</th>
                <th>Total</th>
                <th>Issued</th>
                <th>Due</th>
                <th>Status</th>
                <th className="w-10 whitespace-nowrap">Actions</th>
              </tr>
            </thead>
            <tbody>
              {myFeeNotesQ.isLoading && (
                <tr><td colSpan={9} className="py-6 text-center text-sm text-muted-foreground">Loading fee notes…</td></tr>
              )}
              {!user?.barristerId && (
                <tr><td colSpan={9} className="py-8 text-center text-sm text-muted-foreground">No barrister profile linked to your account.</td></tr>
              )}
              {myFeeNotesQ.isError && (
                <tr><td colSpan={9}><QueryErrorState compact what="your fee notes" onRetry={() => myFeeNotesQ.refetch()} /></td></tr>
              )}
              {myFeeNotesQ.data && myFeeNotesQ.data.feeNotes.length === 0 && (
                <tr><td colSpan={9} className="py-8 text-center text-sm text-muted-foreground">No fee notes raised on your matters yet.</td></tr>
              )}
              {pageSlice(myFeeNotesQ.data?.feeNotes ?? [], page, pageSize).map((n) => (
                <tr key={n.id} className="cursor-pointer">
                  <td className="text-sm font-semibold text-brand-700 dark:text-brand-300">{n.feeNoteNumber}</td>
                  <td className="font-medium text-foreground">{n.case ? `${n.case.caseNumber} ${n.case.caseName}` : '—'}</td>
                  <td className="tabular text-muted-foreground">{formatCurrency(Number(n.subtotal))}</td>
                  <td className="tabular text-xs text-muted-foreground">£{Number(n.vatTotal).toLocaleString('en-GB', { maximumFractionDigits: 2 })}</td>
                  <td className="tabular font-semibold text-foreground">£{Number(n.total).toLocaleString('en-GB', { maximumFractionDigits: 2 })}</td>
                  <td className="text-sm text-muted-foreground">{formatDate(n.issueDate)}</td>
                  <td className="text-sm text-muted-foreground">{formatDate(n.dueDate)}</td>
                  <td><Badge variant={feeNoteStatusVariant(n.status)}>{FEE_NOTE_STATUS_LABEL[n.status] ?? n.status}</Badge></td>
                  <td>
                    <div className="inline-flex items-center gap-0.5">
                      <RowActions
                        onView={() => viewFeeNotePdf(n.id, n.feeNoteNumber)}
                        viewLabel="View PDF"
                      />
                      {isSolo ? (
                          <FeeNoteActionsButton feeNote={n} onChanged={() => myFeeNotesQ.refetch()} />
                      ) : (
                        <FeeNoteDownloadButton feeNote={n} />
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          {(myFeeNotesQ.data?.feeNotes.length ?? 0) > 0 && (
            <TablePagination page={page} pageSize={pageSize} total={myFeeNotesQ.data?.feeNotes.length ?? 0}
              onPageChange={setPage} onPageSizeChange={setPageSize} />
          )}
        </Card>
      )}

      {tab === 'outstanding' && (
        <Card>
          <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th>Client</th>
                <th>Matters</th>
                <th className="text-right">Current</th>
                <th className="text-right">30 days</th>
                <th className="text-right">60 days</th>
                <th className="text-right">90+ days</th>
                <th className="text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {agedDebtQ.isLoading && (
                <tr><td colSpan={7} className="py-6 text-center text-sm text-muted-foreground">Loading aged debt…</td></tr>
              )}
              {/* A 403 here is a decision, not a fault — no Retry, and never "No outstanding
                  balances", which would assert that nothing is owed. */}
              {agedDebtRefused && (
                <tr><td colSpan={7} className="py-8 text-center text-sm text-muted-foreground">
                  Outstanding balances aren't available to your role. Your own fee notes are on the
                  Fee notes tab. Nothing has gone wrong, and there is nothing to retry.
                </td></tr>
              )}
              {agedDebtQ.isError && !agedDebtRefused && (
                <tr><td colSpan={7}><QueryErrorState compact what="outstanding balances" onRetry={() => agedDebtQ.refetch()} /></td></tr>
              )}
              {agedDebtQ.data && agedDebtQ.data.length === 0 && (
                <tr><td colSpan={7} className="py-8 text-center text-sm text-muted-foreground">No outstanding balances.</td></tr>
              )}
              {pageSlice(agedDebtQ.data ?? [], page, pageSize).map((row: AgedDebtRow) => (
                <tr key={row.contactId ?? row.clientName}>
                  <td className="font-medium text-foreground">{row.clientName}</td>
                  <td className="text-muted-foreground">{row.mattersCount}</td>
                  <td className="tabular text-right text-muted-foreground">£{row.current.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</td>
                  <td className="tabular text-right text-amber-600 dark:text-amber-400">£{row.d30.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</td>
                  <td className="tabular text-right text-orange-600 dark:text-orange-400">£{row.d60.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</td>
                  <td className="tabular text-right text-red-600 dark:text-red-400">£{row.d90.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</td>
                  <td className="tabular text-right font-semibold text-foreground">£{row.total.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          {(agedDebtQ.data?.length ?? 0) > 0 && (
            <TablePagination page={page} pageSize={pageSize} total={agedDebtQ.data?.length ?? 0}
              onPageChange={setPage} onPageSizeChange={setPageSize} />
          )}
        </Card>
      )}
      {tab === 'matter' && <FeesByMatterView />}

      <NewFeeNoteModal
        open={feeNoteOpen}
        presetMatterId={emailMatterId ?? undefined}
        onClose={() => { setFeeNoteOpen(false); if (emailMatterId) setSearchParams({}, { replace: true }); }}
        onSave={(note) => {
          toast.success(`Fee note ${note.feeNoteNumber} created`);
          setFeeNoteOpen(false);
          if (emailMatterId) setSearchParams({}, { replace: true });
        }}
      />
    </div>
  );
}

function FeesByMatterView() {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const navigate = useNavigate();
  const { data, isLoading, isError, refetch } = useQuery<FeeMatterSummaryRow[]>({
    queryKey: ['fees-summary-by-matter'],
    queryFn: fetchFeesSummaryByMatter,
    staleTime: 60_000,
  });

  const fmt = (n: number, currency = 'GBP') =>
    new Intl.NumberFormat('en-GB', { style: 'currency', currency, maximumFractionDigits: 2 }).format(n);

  if (isLoading) {
    return <div className="py-12 text-center text-sm text-muted-foreground">Loading…</div>;
  }

  if (isError) {
    return <Card><QueryErrorState what="fees by matter" onRetry={() => refetch()} /></Card>;
  }

  if (!data || data.length === 0) {
    return <div className="py-12 text-center text-sm text-muted-foreground">No fee data found.</div>;
  }

  return (
    <Card>
      <div className="overflow-x-auto">
      <table className="data-table">
        <thead>
          <tr>
            <th>Matter</th>
            <th>Status</th>
            <th className="text-right">Unbilled</th>
            <th className="text-right">Invoiced</th>
            <th className="text-right">Paid</th>
            <th className="text-right">Outstanding</th>
          </tr>
        </thead>
        <tbody>
          {pageSlice(data, page, pageSize).map((row) => (
            <tr
              key={row.matterId}
              className="cursor-pointer hover:bg-muted/40 transition"
              onClick={() => navigate(`/barrister/matters/${row.matterId}`)}
            >
              <td>
                <div className="font-medium text-foreground">{row.matterName}</div>
                <div className="text-xs text-muted-foreground">{row.matterNumber}</div>
              </td>
              <td>
                <Badge variant="outline" className="text-[10px]">{row.status.replace(/_/g, ' ')}</Badge>
              </td>
              <td className="tabular text-right text-muted-foreground">{fmt(row.unbilled, row.currency)}</td>
              <td className="tabular text-right text-foreground">{fmt(row.invoiced, row.currency)}</td>
              <td className="tabular text-right text-emerald-700 dark:text-emerald-300">{fmt(row.paid, row.currency)}</td>
              <td className={`tabular text-right font-semibold ${row.outstanding > 0 ? 'text-amber-700 dark:text-amber-300' : 'text-muted-foreground'}`}>
                {fmt(row.outstanding, row.currency)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      <TablePagination page={page} pageSize={pageSize} total={data.length}
        onPageChange={setPage} onPageSizeChange={setPageSize} />
    </Card>
  );
}
